import { isEmpty } from 'lodash';

interface Company {
  isVCT?: boolean;
  tradingName?: string | null;
  shareClass?: string | null;
  firmId?: {
    firmName: string;
  };
  fundId?: {
    fundName: string;
  };
}

interface Options {
  includeShareClass?: boolean;
  includeFirmName?: boolean;
}

export const getCompanyLabel = (company?: Company, options?: Options) => {
  if (!company || isEmpty(company)) return '';
  const { includeShareClass = true, includeFirmName = false } = options ?? {};
  const { isVCT, tradingName, shareClass, firmId, fundId } = company;

  const shareClassLabel = shareClass?.length ? `(${shareClass})` : '';
  const firmNameLabel = firmId?.firmName ? `${firmId.firmName} - ` : '';

  if (isVCT) {
    return `${firmNameLabel}${fundId?.fundName ?? ''} ${shareClassLabel}`;
  }

  if (!includeFirmName) {
    return includeShareClass
      ? `${tradingName ?? ''} ${shareClassLabel}`
      : tradingName;
  }

  return includeShareClass
    ? `${firmNameLabel}${tradingName ?? ''} ${shareClassLabel}`
    : `${firmNameLabel}${tradingName ?? ''}`;
};

/**
 * Gets the company label with share class information.
 * Returns format: "{tradingName} ({singleShareClassLabel ?? 'Multiple share classes'})"
 *
 * @param tradingName - The company's trading name
 * @param singleShareClassLabel - The share class label if there's a single share class, or null/undefined if multiple
 * @returns The formatted company label with share class information
 *
 * @example
 * getCompanyLabelWithShareClass('Connect Earth', 'Ordinary')
 * // Returns: "Connect Earth (Ordinary)"
 *
 * @example
 * getCompanyLabelWithShareClass('Connect Earth', null)
 * // Returns: "Connect Earth (Multiple share classes)"
 */
export const getExitedCompanyLabelWithShareClass = (
  tradingName: string | null | undefined,
  singleShareClassLabel?: string | null,
): string => {
  const companyName = tradingName ?? '';
  const shareClassLabel = singleShareClassLabel ?? 'Multiple share classes';
  return `${companyName} (${shareClassLabel})`;
};
